// Global Instructions Rule Applied!
//
// Email Design Template — full-form detail.
//
// Smaller sibling of FullEmailCampaignForm: same Jodit body editor, same
// save shape, but no recipients / sending / status state — a template is
// a pure body+metadata artifact.
//
// Body source (v1): only 'jodit' is exercised here. The 'imported' path
// (paste Stripo HTML, bypass sanitize) ships in the Stripo-import session;
// the body_source picker is still rendered (read-only label for 'imported'
// templates that arrive via the future import affordance), so the schema
// is compatible from day one.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Form, Input, Card, Row, Col, Spin, Typography, Space, Button, Modal, Tooltip, Tag, Badge, Alert } from 'antd'
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  ImportOutlined,
  TagsOutlined,
  CopyOutlined,
  PaperClipOutlined
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { store } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { addFormTab, removeTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import RichTextEditor from '../../../../core/components/RichTextEditor.jsx'
import type { CKEditorRichTextHandle } from '../../../../core/components/CKEditorRichText.tsx'
import { useEditorEngine, useEditorConfigReady } from '../../../global_configs/hooks/useEditorConfig.ts'
import { ShadowRichHtml } from '../../../../core/components/ShadowRichHtml.tsx'
import { FileUploadManager } from '../../../../core/components/FileUploadManager/index.tsx'
import {
  TABLE_FILE_URLS_EMAIL_DESIGN_TEMPLATES,
  FIELD_EMAIL_DESIGN_TEMPLATE_ID
} from '../../../../state_manager/globals.ts'
import type {
  EmailDesignTemplateModel,
  EmailDesignTemplateAttachment
} from '../../../../state_manager/data_models/email_design_template_model.ts'
import {
  fetchEmailDesignTemplateDetail,
  createEmailDesignTemplate,
  updateEmailDesignTemplate,
  type EmailDesignTemplateSavePayload
} from '../../services/emailDesignTemplateService.ts'
import { MERGE_FIELDS, DEAL_MERGE_FIELDS } from '../../constants/mergeFields.ts'

const { Title, Text } = Typography

const TEMPLATES_LIST_PATH = '/email-design-templates'

// Lazy-loaded so the CKEditor wrapper is code-split off the main bundle (the
// editor build itself loads from the CDN). Mirrors Full.tsx.
const CKEditorRichText = React.lazy(
  () => import('../../../../core/components/CKEditorRichText.tsx')
)

interface TemplateFullProps {
  recordId?: string | null
  isNew?: boolean
}

interface TemplateFormValues {
  name: string
  subject_default: string
  body_html: string
}

export default function TemplateFull({
  recordId: propRecordId,
  isNew: propIsNew
}: TemplateFullProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [form] = Form.useForm<TemplateFormValues>()
  // Compose-body editor engine (cfg-driven). 'ckeditor' swaps in CKEditor and
  // makes save inline-at-save + write body_source='ckeditor'.
  const editorEngine = useEditorEngine()
  // Gate the editor/tag until globalConfigs has loaded so a cold-loaded form
  // doesn't flash the Jodit editor + "Jodit" tag before the engine resolves.
  const engineReady = useEditorConfigReady()
  const editorRef = useRef<CKEditorRichTextHandle | null>(null)

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNewInitial =
    propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId

  // Must match useTabManager's parseRoute/addTab key pattern exactly
  // (`${module}-form-${new|edit}-${recordId}`), otherwise removeTab from
  // closeToList doesn't find the tab and Cancel / Back become no-ops.
  const formTabKey = `email-design-templates-form-${
    isNewInitial ? 'new' : 'edit'
  }-${recordId || 'new'}`

  const [loading, setLoading] = useState(!isNewInitial)
  const [saving, setSaving] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [persistedId, setPersistedId] = useState<string | null>(
    isNewInitial ? null : recordId === 'new' ? null : recordId
  )
  const isEditing = !!persistedId

  // Imported templates display as read-only HTML — Jodit would re-normalize
  // them. For v1 we only create Jodit-source templates inside the app.
  const [bodySource, setBodySource] = useState<'jodit' | 'imported' | 'ckeditor'>('jodit')
  // Live mirror of the body for the preview pane — onLiveChange updates it on
  // every keystroke so the preview tracks edits live (not only on blur/save).
  const [livePreviewBody, setLivePreviewBody] = useState<string>('')
  const [mergeFieldsOpen, setMergeFieldsOpen] = useState(false)
  const [hoveredMergeField, setHoveredMergeField] = useState<string | null>(null)
  // Template-level attachments (file_urls_email_design_templates rows).
  // Copied onto a campaign when the template is applied in the compose
  // form. The modal mounts the generic FileUploadManager; this list mirrors
  // the detail RPC's attachments array for the chip strip + badge count.
  const [attachmentsOpen, setAttachmentsOpen] = useState(false)
  const [templateAttachments, setTemplateAttachments] = useState<
    EmailDesignTemplateAttachment[]
  >([])

  // ─── Mount-time fetch ────────────────────────────────────────────────
  useEffect(() => {
    if (isNewInitial) {
      dispatch(updateTabTitle({ key: formTabKey, title: 'New Template' }))
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchEmailDesignTemplateDetail(String(recordId), token)
      if (cancelled) return
      if (res.error) {
        showErrorToast(res.error)
        setLoading(false)
        return
      }
      const d: EmailDesignTemplateModel | null = res.data
      if (!d) {
        setNotFound(true)
        setLoading(false)
        return
      }
      const src =
        d.email_design_templates__body_source === 'imported'
          ? 'imported'
          : d.email_design_templates__body_source === 'ckeditor'
          ? 'ckeditor'
          : 'jodit'
      setBodySource(src)
      form.setFieldsValue({
        name: d.email_design_templates__name,
        subject_default: d.email_design_templates__subject_default,
        body_html: d.email_design_templates__body_html ?? ''
      })
      setLivePreviewBody(d.email_design_templates__body_html ?? '')
      setTemplateAttachments(d.email_design_templates__attachments ?? [])
      setPersistedId(d.email_design_templates__id)
      dispatch(
        updateTabTitle({
          key: formTabKey,
          title: `Edit ${d.email_design_templates__name || 'Template'}`
        })
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [recordId, isNewInitial, form, dispatch, formTabKey])

  // ─── Body / preview wiring ───────────────────────────────────────────
  const handleBodyChange = useCallback(
    (next: string) => {
      form.setFieldsValue({ body_html: next })
    },
    [form]
  )

  // Refetch the attachments list after the modal closes so the chip strip
  // + badge pick up any add / remove operations the modal performed
  // (mirrors Full.tsx's refreshCampaignStatus-on-close).
  const refreshAttachments = useCallback(async () => {
    if (!persistedId) return
    const token = getAccessToken(store.getState().auth)
    const res = await fetchEmailDesignTemplateDetail(persistedId, token)
    if (res.error || !res.data) return
    setTemplateAttachments(res.data.email_design_templates__attachments ?? [])
  }, [persistedId])

  // ─── Save / cancel ───────────────────────────────────────────────────
  const closeToList = useCallback(() => {
    navigate(TEMPLATES_LIST_PATH)
    dispatch(removeTab({ key: formTabKey }))
  }, [navigate, dispatch, formTabKey])

  const handleSave = useCallback(async () => {
    let values: TemplateFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      // Resolve body_source + body_html. 'imported' ships verbatim; the
      // CKEditor engine tags 'ckeditor' and inline-at-saves (plan §6.1) — a
      // template is a deliberate inline-styled snapshot; else legacy 'jodit'.
      let resolvedSource: 'jodit' | 'ckeditor' | 'imported'
      let resolvedBody = values.body_html || ''
      if (bodySource === 'imported') {
        resolvedSource = 'imported'
      } else if (editorEngine === 'ckeditor') {
        resolvedSource = 'ckeditor'
        try {
          const inline = await editorRef.current?.getInlineHtml()
          if (inline && inline.trim()) resolvedBody = inline
        } catch {
          /* keep the class-based body fallback */
        }
      } else {
        resolvedSource = 'jodit'
      }
      const payload: EmailDesignTemplateSavePayload = {
        name: values.name.trim(),
        subject_default: values.subject_default || '',
        body_source: resolvedSource,
        body_html: resolvedBody
      }
      const result = isEditing
        ? await updateEmailDesignTemplate(String(persistedId), payload, token)
        : await createEmailDesignTemplate(payload, token)

      if (result.error || !result.data) {
        showErrorToast(result.error || 'Failed to save template')
        return
      }
      toast.success(isEditing ? 'Template updated' : 'Template created')
      const newId = result.data.email_design_templates__id
      setPersistedId(newId)
      // After first create, transition the URL from /new to /<uuid>
      // with replace:true so the back button doesn't return to the
      // empty /new form and a hard-refresh lands on the saved
      // template instead of starting over. Mirrors the fix applied
      // to Full.tsx in PR #57.
      if (!isEditing && newId) {
        const cleanName = values.name.trim() || 'Template'
        navigate(`/email-design-templates/forms/full/${newId}`, { replace: true })
        // The new→edit URL change makes useTabManager spawn a fresh
        // `…-form-edit-<id>` tab; without cleanup the original
        // `…-form-new-new` tab lingers (it shows in the tab bar) and steals
        // focus on close — closeToList's removeTab drops the edit tab and
        // tabSlice then activates the leftover new tab, so the first
        // back-arrow surfaces an empty New-Template form instead of closing
        // (the "first click doesn't close" bug). Open the edit tab and drop
        // the stale new tab here. add-before-remove keeps the new-new tab
        // off the active slot when removed → no flash, no leftover tab.
        dispatch(
          addFormTab({
            module: 'email-design-templates',
            recordId: newId,
            isNew: false,
            title: cleanName
          })
        )
        dispatch(removeTab({ key: formTabKey }))
        dispatch(
          updateTabTitle({
            key: `email-design-templates-form-edit-${newId}`,
            title: `Edit ${cleanName}`
          })
        )
      } else {
        dispatch(
          updateTabTitle({
            key: formTabKey,
            title: `Edit ${values.name.trim() || 'Template'}`
          })
        )
      }
    } finally {
      setSaving(false)
    }
  }, [form, isEditing, persistedId, bodySource, editorEngine, dispatch, formTabKey, navigate])

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className='h-full overflow-auto bg-white'>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={closeToList} />
          <Title level={4} style={{ margin: 0 }}>
            {isEditing ? 'Edit Template' : 'New Template'}
          </Title>
          {bodySource === 'imported' ? (
            <Tag icon={<ImportOutlined />} color='purple'>
              Imported HTML
            </Tag>
          ) : !engineReady ? null : editorEngine === 'ckeditor' ? (
            <Tag icon={<FileTextOutlined />} color='geekblue'>
              CKEditor
            </Tag>
          ) : (
            <Tag icon={<FileTextOutlined />} color='blue'>
              Jodit
            </Tag>
          )}
        </Space>
        <Space>
          <Button onClick={closeToList}>Cancel</Button>
          <Button
            type='primary'
            loading={saving}
            onClick={handleSave}
            disabled={notFound}
          >
            {isEditing ? 'Save' : 'Create template'}
          </Button>
        </Space>
      </div>

      {notFound ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
          This template was not found (it may have been deleted).
        </div>
      ) : (
        <Spin spinning={loading}>
          <div style={{ padding: '12px 16px 16px' }}>
            <Form
              form={form}
              layout='vertical'
              autoComplete='off'
              initialValues={{ name: '', subject_default: '', body_html: '' }}
            >
              <Form.Item name='body_html' hidden>
                <Input />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24}>
                  <Card size='small' title='Template metadata' style={{ marginBottom: 12 }}>
                    <Row gutter={12}>
                      <Col xs={24} md={10}>
                        <Form.Item
                          name='name'
                          label='Template name'
                          rules={[
                            { required: true, message: 'Required' },
                            { whitespace: true, message: 'Required' }
                          ]}
                          tooltip='Operator-only label. Not seen by recipients. Pick something descriptive — this is what shows up in the "Start from template" picker.'
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            placeholder='e.g. Quarterly LP Update'
                            maxLength={200}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={14}>
                        <Form.Item
                          name='subject_default'
                          label='Default subject'
                          tooltip='Pre-fills the campaign subject when starting from this template. Can use merge fields like {{team_name}}.'
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            placeholder='e.g. {{team_name}} — Q2 2026 update'
                            maxLength={500}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={14}>
                  <Card
                    size='small'
                    title={bodySource === 'imported' ? 'Body (imported HTML — read-only)' : 'Body'}
                    style={{ marginBottom: 12, minHeight: 480 }}
                    bodyStyle={{ padding: 0, minHeight: 480 }}
                    extra={
                      <Space size={6}>
                        {bodySource !== 'imported' && (
                          <Tooltip title='Insert a merge field placeholder'>
                            <Button
                              size='small'
                              icon={<TagsOutlined />}
                              onClick={() => setMergeFieldsOpen(true)}
                            >
                              Merge fields
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip
                          title={
                            isEditing
                              ? 'Manage attachments (copied into a campaign when this template is loaded)'
                              : 'Save the template first to add attachments'
                          }
                        >
                          {/* Wrapping span so Tooltip still fires when the
                              underlying Button is disabled (Antd quirk). */}
                          <span>
                            <Badge count={templateAttachments.length} size='small' offset={[-4, 4]}>
                              <Button
                                size='small'
                                icon={<PaperClipOutlined />}
                                onClick={() => setAttachmentsOpen(true)}
                                disabled={!isEditing}
                              >
                                Attachments
                              </Button>
                            </Badge>
                          </span>
                        </Tooltip>
                      </Space>
                    }
                  >
                    {bodySource === 'imported' ? (
                      <div
                        style={{
                          padding: '12px 16px',
                          height: 480,
                          overflow: 'auto',
                          background: '#fafafa',
                          fontFamily: 'monospace',
                          fontSize: 12,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        <Text type='secondary'>
                          Imported templates display their HTML source. Re-import to update.
                        </Text>
                        <pre style={{ marginTop: 12, fontSize: 11 }}>
                          {form.getFieldValue('body_html') || ''}
                        </pre>
                      </div>
                    ) : (
                      <div style={{ height: 480 }}>
                        {!engineReady ? (
                          <div style={{ padding: 24, textAlign: 'center' }}>
                            <Spin />
                          </div>
                        ) : editorEngine === 'ckeditor' ? (
                          <React.Suspense
                            fallback={
                              <div style={{ padding: 24, textAlign: 'center' }}>
                                <Spin />
                              </div>
                            }
                          >
                            <CKEditorRichText
                              ref={editorRef}
                              value={form.getFieldValue('body_html') || ''}
                              onChange={handleBodyChange}
                              onLiveChange={setLivePreviewBody}
                              placeholder='Compose the template body…'
                              imageUpload={false}
                            />
                          </React.Suspense>
                        ) : (
                          <RichTextEditor
                            value={form.getFieldValue('body_html') || ''}
                            onChange={handleBodyChange}
                            onLiveChange={setLivePreviewBody}
                            placeholder='Compose the template body…'
                          />
                        )}
                      </div>
                    )}
                    {templateAttachments.length > 0 && (
                      <div
                        style={{
                          padding: '6px 12px',
                          borderTop: '1px solid #f0f0f0',
                          background: '#fafafa'
                        }}
                      >
                        <Space size={6} wrap>
                          <PaperClipOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                          <Text type='secondary' style={{ fontSize: 12 }}>Attached:</Text>
                          {templateAttachments.map((a) => (
                            <Tag
                              key={a.id}
                              style={{ cursor: 'pointer', margin: 0 }}
                              onClick={() => setAttachmentsOpen(true)}
                            >
                              {a.file_name}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    )}
                  </Card>
                </Col>
                <Col xs={24} md={10}>
                  <Card
                    size='small'
                    title='Preview'
                    style={{ marginBottom: 12, minHeight: 480 }}
                    bodyStyle={{ padding: 0, height: 480, overflow: 'auto' }}
                  >
                    {/* Driven by livePreviewBody (onLiveChange) so the preview
                        tracks keystrokes, not just blur/save. */}
                    <ShadowRichHtml
                      html={livePreviewBody || ''}
                      className='template-preview'
                    />
                  </Card>
                </Col>
              </Row>
              {/* Attachments modal — drop-in FileUploadManager against
                  file_urls_email_design_templates (mirrors Full.tsx's
                  campaign attachments modal). On close we refetch the
                  template detail so the chip strip + badge count pick up
                  any add / remove operations the modal performed. */}
              <Modal
                title='Attachments'
                open={attachmentsOpen}
                onCancel={() => {
                  setAttachmentsOpen(false)
                  refreshAttachments()
                }}
                footer={null}
                width={720}
                destroyOnClose
              >
                <Alert
                  type='info'
                  showIcon
                  style={{ marginBottom: 12 }}
                  message='Attachments are stored on the template and copied into a campaign when the template is loaded.'
                />
                {isEditing && persistedId ? (
                  <FileUploadManager
                    files={[]}
                    tableName={TABLE_FILE_URLS_EMAIL_DESIGN_TEMPLATES}
                    idField={FIELD_EMAIL_DESIGN_TEMPLATE_ID}
                    idValue={persistedId}
                  />
                ) : (
                  <Alert
                    type='warning'
                    showIcon
                    message='Save the template first to add attachments.'
                  />
                )}
              </Modal>

              {/* Merge fields modal — same chip strip as the campaign
                  compose form (constants/mergeFields.ts is the shared
                  source of truth). */}
              <Modal
                title='Merge fields'
                open={mergeFieldsOpen}
                onCancel={() => setMergeFieldsOpen(false)}
                footer={null}
                width={520}
                destroyOnClose
              >
                <p style={{ marginTop: 0, color: '#8c8c8c' }}>
                  Click a placeholder to copy. Paste it into the subject default or
                  body where you want the dynamic value to appear. Campaign fields
                  are filled when a campaign uses this template; deal fields are
                  filled when a stage action (Configuration → Stage Actions) sends
                  it.
                </p>
                {[
                  { key: 'campaign', title: 'Campaign fields', fields: MERGE_FIELDS },
                  { key: 'deal', title: 'Deal fields (stage actions)', fields: DEAL_MERGE_FIELDS }
                ].map((section) => (
                  <div key={section.key} style={{ marginBottom: 12 }}>
                    <p style={{ marginBottom: 4, fontWeight: 600 }}>{section.title}</p>
                    <Space wrap size={[6, 8]}>
                      {section.fields.map((f) => {
                        const isHovered = hoveredMergeField === f.placeholder
                        return (
                          <Tooltip title={f.tooltip} key={f.placeholder}>
                            <Tag
                              onMouseEnter={() => setHoveredMergeField(f.placeholder)}
                              onMouseLeave={() =>
                                setHoveredMergeField((curr) =>
                                  curr === f.placeholder ? null : curr
                                )
                              }
                              style={{
                                cursor: 'pointer',
                                fontFamily: 'monospace',
                                userSelect: 'none',
                                borderColor: isHovered ? '#1677ff' : undefined,
                                background: isHovered ? '#e6f4ff' : undefined,
                                margin: 0
                              }}
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(f.placeholder)
                                  toast.success(`Copied ${f.placeholder}`)
                                  setMergeFieldsOpen(false)
                                } catch {
                                  showErrorToast(
                                    'Clipboard copy failed — select and copy manually.'
                                  )
                                }
                              }}
                            >
                              {f.placeholder}
                              <CopyOutlined
                                style={{
                                  marginLeft: 6,
                                  fontSize: 11,
                                  opacity: isHovered ? 1 : 0,
                                  transition: 'opacity 120ms ease',
                                  color: '#1677ff'
                                }}
                              />
                            </Tag>
                          </Tooltip>
                        )
                      })}
                    </Space>
                  </div>
                ))}
              </Modal>
            </Form>
          </div>
        </Spin>
      )}
    </div>
  )
}
